import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { billingAPI } from '../lib/api';

const STATUS_LABELS = {
  trial: { label: 'Trial', color: 'text-blue-400' },
  active: { label: 'Active', color: 'text-green-400' },
  past_due: { label: 'Past due — payment failed', color: 'text-red-400' },
  cancelled: { label: 'Cancelled', color: 'text-orange-400' },
  trial_expired: { label: 'Trial expired', color: 'text-red-400' },
};

function money({ amount, currency }) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

function daysUntil(date) {
  if (!date) return null;
  const ms = new Date(date) - new Date();
  return ms <= 0 ? 0 : Math.ceil(ms / 86400_000);
}

export default function Billing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success') === '1';
  const cancelled = searchParams.get('cancelled') === '1';
  const welcome = searchParams.get('welcome') === '1';
  const orgParam = searchParams.get('org');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notMember, setNotMember] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    billingAPI.status(orgParam)
      .then(res => setStatus(res.data))
      .catch(err => {
        if (err.response?.status === 404) setNotMember(true);
        else setError(err.response?.data?.error || 'Failed to load billing status');
      })
      .finally(() => setLoading(false));
  }, [orgParam]);

  async function handleCheckout() {
    setCheckoutLoading(true);
    setError('');
    try {
      const res = await billingAPI.createCheckout(status.organisation_id);
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  }

  async function handlePortal() {
    setPortalLoading(true);
    setError('');
    try {
      const res = await billingAPI.portal(status.organisation_id);
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  }

  const trialExpired = status?.subscription_status === 'trial' && daysUntil(status.trial_ends_at) === 0;
  const subscriptionStatus = trialExpired ? 'trial_expired' : status?.subscription_status;
  const statusInfo = STATUS_LABELS[subscriptionStatus] || STATUS_LABELS.trial;
  const isTrial = subscriptionStatus === 'trial';
  const trialDays = isTrial ? daysUntil(status?.trial_ends_at) : null;
  const isOwner = status?.your_role === 'owner';
  const canSubscribe = isOwner && (!status?.has_subscription || status?.subscription_status === 'cancelled');

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-2xl mx-auto px-6 py-16">

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate('/')} className="text-gray-600 hover:text-white transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-red-600 rounded flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full" />
            </div>
            <span className="font-bold">Mission Control</span>
          </div>
          <h1 className="text-lg font-semibold ml-2">Billing</h1>
        </div>

        {/* Welcome / trial started banner */}
        {welcome && (
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg px-5 py-5 mb-6">
            <div className="font-semibold text-blue-300 mb-1">Welcome to Mission Control!</div>
            <p className="text-blue-400 text-sm mb-3">Your 14-day free trial has started — you have full access. No payment required yet.</p>
            <p className="text-gray-500 text-xs">Subscribe below to keep access after your trial ends, or continue and we&rsquo;ll remind you before it expires.</p>
          </div>
        )}

        {/* Success banner */}
        {success && (
          <div className="bg-green-900/30 border border-green-700 rounded-lg px-5 py-4 mb-6">
            <div className="font-semibold text-green-300 mb-1">You&rsquo;re all set!</div>
            <p className="text-green-400 text-sm">Your subscription is active. A receipt has been sent to your email.</p>
          </div>
        )}

        {/* Cancelled banner */}
        {cancelled && (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg px-5 py-4 mb-6">
            <p className="text-gray-400 text-sm">Checkout cancelled — no charge was made.</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded px-4 py-3 text-red-300 text-sm mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-gray-600 text-sm">Loading...</div>
        ) : notMember ? (
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg p-6">
            <p className="text-sm text-gray-400 mb-4">
              Billing belongs to an account, and you&rsquo;re not a member of one — you&rsquo;re working as a guest.
            </p>
            <button onClick={() => navigate('/account')} className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm font-medium rounded transition-colors">
              Set up your own account
            </button>
          </div>
        ) : status && (
          <>
            {/* Current plan */}
            <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg p-6 mb-6">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">{status.organisation_name}</h2>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className={`text-sm ${statusInfo.color}`}>
                    {statusInfo.label}
                    {isTrial && trialDays !== null && (
                      <span className="text-gray-500 ml-2">— {trialDays} day{trialDays !== 1 ? 's' : ''} remaining</span>
                    )}
                  </div>
                  <div className="text-2xl font-bold mt-2">
                    {money({ ...status.seat_price, amount: status.monthly_total })}
                    <span className="text-sm font-normal text-gray-500">/{status.seat_price.interval}</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">
                    {status.seats} seat{status.seats !== 1 ? 's' : ''} × {money(status.seat_price)} per seat
                  </p>
                </div>

                {isOwner && status.has_subscription && (
                  <button
                    onClick={handlePortal}
                    disabled={portalLoading}
                    className="flex-shrink-0 px-4 py-2 bg-[#1e1e1e] hover:bg-[#2a2a2a] border border-[#3a3a3a] rounded text-sm transition-colors disabled:opacity-50"
                  >
                    {portalLoading ? 'Opening...' : 'Manage billing'}
                  </button>
                )}
              </div>
              <button onClick={() => navigate(`/account?org=${status.organisation_id}`)} className="text-xs text-gray-500 hover:text-white underline mt-4">
                Manage members and seats
              </button>
            </div>

            {/* How it's priced */}
            <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg p-6 mb-6">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">How it works</h2>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex gap-2"><span className="text-green-500">✓</span>{money(status.seat_price)} per member per month — every feature included</li>
                <li className="flex gap-2"><span className="text-green-500">✓</span>Adding or removing a member adjusts the bill, prorated</li>
                <li className="flex gap-2"><span className="text-green-500">✓</span>Guests are free: invite commentators and freelancers to a workspace</li>
                <li className="flex gap-2"><span className="text-green-500">✓</span>vMix, Singular.live, cue blocks, unlimited workspaces and shows</li>
              </ul>
            </div>

            {/* Subscribe */}
            {canSubscribe && (
              <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg p-6 mb-6">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  {isTrial ? 'Subscribe before your trial ends' : 'Subscribe'}
                </h2>
                <p className="text-gray-500 text-xs mb-4">
                  {status.seats} seat{status.seats !== 1 ? 's' : ''} today — {money({ ...status.seat_price, amount: status.monthly_total })} a month.
                  {isTrial && ' Your trial runs to its end before the first charge.'}
                </p>
                {status.seat_billing_configured ? (
                  <button
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                    className="w-full py-2 text-sm bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50 font-medium"
                  >
                    {checkoutLoading ? 'Loading...' : 'Subscribe'}
                  </button>
                ) : (
                  <p className="text-xs text-amber-500">Online subscription is not set up on this server yet.</p>
                )}
                <p className="text-gray-600 text-xs mt-3">All prices exclude VAT.</p>
              </div>
            )}

            {!isOwner && (
              <p className="text-center text-xs text-gray-600">Only the account owner can change billing.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
